// modules/MultiplayerPlayerManager/PositionSync.js
// 多人玩家位置同步模块

import { GAME_CONSTANTS } from '../GameConstants.js';

export default class PositionSync {
    constructor(scene, networkManager) {
        this.scene = scene;
        this.networkManager = networkManager;
        
        const mp = GAME_CONSTANTS.MULTIPLAYER;
        this.positionUpdateInterval = mp.POSITION_UPDATE_INTERVAL_MS;
        this.minSendDistanceSq = mp.MIN_SEND_DISTANCE_SQ;
        this.remoteLerpMs = mp.REMOTE_LERP_MS;
        this.remoteSnapDistance = mp.REMOTE_SNAP_DISTANCE;
        
        this.lastPositionUpdate = 0;
        this.lastSentPosition = null;
        
        this.playerLastPositions = new Map();
        this.playerDirections = new Map();
        this.playerLastMovingState = new Map();
        /** @type {Map<string, { targetX: number, targetY: number, direction?: string }>} */
        this.remoteTargets = new Map();
    }
    
    updateLocalPlayerPosition(x, y, direction, isLocalPlayerLeader, teamLeaderId) {
        if (teamLeaderId && !isLocalPlayerLeader) {
            return;
        }
        
        const now = Date.now();
        if (now - this.lastPositionUpdate < this.positionUpdateInterval) {
            return;
        }

        if (this.lastSentPosition) {
            const dx = x - this.lastSentPosition.x;
            const dy = y - this.lastSentPosition.y;
            if (dx * dx + dy * dy < this.minSendDistanceSq) {
                return;
            }
        }
        
        this.lastPositionUpdate = now;
        this.lastSentPosition = { x, y };
        
        if (this.networkManager && this.networkManager.isConnected()) {
            this.networkManager.updatePlayerPosition(x, y, direction);
        }
    }
    
    /**
     * @param {string} playerId
     * @param {number} x
     * @param {number} y
     * @param {string} [direction]
     */
    setRemoteTarget(playerId, x, y, direction) {
        const target = this.remoteTargets.get(playerId) || {};
        target.targetX = x;
        target.targetY = y;
        if (direction) {
            target.direction = direction;
        }
        this.remoteTargets.set(playerId, target);
    }

    /**
     * @param {import('./PlayerRenderer.js').default} playerRenderer
     * @param {number} delta
     */
    tickRemoteInterpolation(playerRenderer, delta) {
        if (!playerRenderer || this.remoteTargets.size === 0) {
            return;
        }

        const lerpFactor = Math.min(1, delta / this.remoteLerpMs);

        this.remoteTargets.forEach((target, playerId) => {
            const sprite = playerRenderer.getPlayerSprite(playerId);
            if (!sprite || target.targetX === undefined || target.targetY === undefined) {
                return;
            }

            const dx = target.targetX - sprite.x;
            const dy = target.targetY - sprite.y;
            const distanceSq = dx * dx + dy * dy;

            if (distanceSq >= this.remoteSnapDistance * this.remoteSnapDistance) {
                sprite.x = target.targetX;
                sprite.y = target.targetY;
            } else if (distanceSq > 0.01) {
                sprite.x += dx * lerpFactor;
                sprite.y += dy * lerpFactor;
            }

            const nameTag = playerRenderer.playerNameTags.get(playerId);
            if (nameTag) {
                nameTag.x = sprite.x;
                nameTag.y = sprite.y - 30;
            }

            if (target.direction) {
                sprite.setData('direction', target.direction);
            }

            this.playerLastPositions.set(playerId, { x: sprite.x, y: sprite.y });
        });
    }
    
    /**
     * @param {string} playerId
     * @param {Object} playerData
     * @param {Phaser.GameObjects.Sprite} sprite
     * @param {Phaser.GameObjects.Text} nameTag
     * @param {boolean} [immediate=false]
     * @returns {Object}
     */
    updateRemotePlayerPosition(playerId, playerData, sprite, nameTag, immediate = false) {
        if (!sprite) {
            return { updated: false, isMoving: false, direction: 'down' };
        }
        
        let updated = false;
        let isMoving = false;
        let direction = playerData.direction || 'down';
        
        if (playerData.x !== undefined && playerData.y !== undefined) {
            if (immediate) {
                sprite.x = playerData.x;
                sprite.y = playerData.y;
                if (nameTag) {
                    nameTag.x = playerData.x;
                    nameTag.y = playerData.y - 30;
                }
                this.remoteTargets.delete(playerId);
            } else {
                this.setRemoteTarget(playerId, playerData.x, playerData.y, playerData.direction);
            }
            updated = true;
        }
        
        if (playerData.direction) {
            sprite.setData('direction', playerData.direction);
            this.playerDirections.set(playerId, playerData.direction);
            direction = playerData.direction;
        }
        
        const lastPos = this.playerLastPositions.get(playerId) || { x: sprite.x, y: sprite.y };
        const dx = sprite.x - lastPos.x;
        const dy = sprite.y - lastPos.y;
        const distanceSq = dx * dx + dy * dy;
        isMoving = distanceSq > 0.5;
        
        this.playerLastPositions.set(playerId, { x: sprite.x, y: sprite.y });
        
        return { updated, isMoving, direction };
    }
    
    getLastPosition(playerId) {
        return this.playerLastPositions.get(playerId) || { x: 0, y: 0 };
    }
    
    setPosition(playerId, x, y) {
        this.playerLastPositions.set(playerId, { x, y });
    }
    
    getDirection(playerId) {
        return this.playerDirections.get(playerId) || 'down';
    }
    
    setDirection(playerId, direction) {
        this.playerDirections.set(playerId, direction);
    }
    
    getMovingState(playerId) {
        return this.playerLastMovingState.get(playerId) || false;
    }
    
    setMovingState(playerId, isMoving) {
        this.playerLastMovingState.set(playerId, isMoving);
    }
    
    clearPlayerData(playerId) {
        this.playerLastPositions.delete(playerId);
        this.playerDirections.delete(playerId);
        this.playerLastMovingState.delete(playerId);
        this.remoteTargets.delete(playerId);
    }
}
